/**
 * 레거시 `ContractPdfPreview.js` 포팅.
 * finalPdfDownloadUrl(또는 download-url API)로 계약 PDF를 iframe 미리보기한다.
 */
import { consignmentContractService } from '@/entities/contract/api/consignment-contract.api';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Button } from '@/shared/components/ui/button';
import { Download } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

const resolveFinalPdfPreviewUrl = async ({
  contractId,
  finalPdfDownloadUrl,
}: {
  contractId?: string | null;
  finalPdfDownloadUrl?: string | null;
}): Promise<string> => {
  let url =
    finalPdfDownloadUrl != null && String(finalPdfDownloadUrl).trim() !== ''
      ? String(finalPdfDownloadUrl).trim()
      : null;

  if (!url && contractId) {
    url = await consignmentContractService.fetchFinalPdfDownloadUrl(contractId);
  }

  if (!url) {
    throw new Error('계약서 PDF URL을 확인할 수 없습니다.');
  }
  return url;
};

const fetchPdfBlobFromUrl = async (url: string): Promise<Blob> => {
  try {
    const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
    if (!res.ok) throw new Error(`PDF 조회 실패 (${res.status})`);
    const blob = await res.blob();
    if (!blob.size) throw new Error('PDF 내용이 비어 있습니다.');
    return blob;
  } catch (e) {
    if (e instanceof TypeError) {
      throw new Error('S3 CORS');
    }
    throw e;
  }
};

export interface ContractPdfPreviewProps {
  contractId?: string | null;
  finalPdfDownloadUrl?: string | null;
  downloadName?: string;
  title?: string;
}

export const ContractPdfPreview: React.FC<ContractPdfPreviewProps> = ({
  contractId,
  finalPdfDownloadUrl,
  title = '판촉 영업 위탁 계약서 미리보기',
}) => {
  const [previewSrc, setPreviewSrc] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasSource = Boolean(
    contractId ||
      (finalPdfDownloadUrl != null && String(finalPdfDownloadUrl).trim() !== ''),
  );

  useEffect(() => {
    if (!hasSource) {
      setPreviewSrc('');
      setError(null);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = '';

    const load = async () => {
      setLoading(true);
      setError(null);
      setPreviewSrc('');
      try {
        const url = await resolveFinalPdfPreviewUrl({
          contractId,
          finalPdfDownloadUrl,
        });
        if (cancelled) return;
        try {
          const blob = await fetchPdfBlobFromUrl(url);
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setPreviewSrc(objectUrl);
        } catch (fetchError) {
          if (cancelled) return;
          if (
            fetchError instanceof Error &&
            fetchError.message.includes('S3 CORS')
          ) {
            setPreviewSrc(url);
            return;
          }
          throw fetchError;
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'PDF를 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [hasSource, contractId, finalPdfDownloadUrl]);

  const handleDownload = useCallback(async () => {
    try {
      const url = await resolveFinalPdfPreviewUrl({
        contractId,
        finalPdfDownloadUrl,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(e instanceof Error ? e.message : '다운로드에 실패했습니다.');
    }
  }, [contractId, finalPdfDownloadUrl]);

  if (!hasSource) {
    return (
      <p className="text-sm text-muted-foreground">
        계약서 파일이 등록되면 미리보기가 표시됩니다.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading || Boolean(error)}
          onClick={() => void handleDownload()}
        >
          <Download className="mr-2 h-4 w-4" />
          다운로드
        </Button>
        {title ? (
          <span className="text-sm text-muted-foreground">{title}</span>
        ) : null}
      </div>
      {loading ? <LoadingSpinner tip="계약서 PDF를 불러오는 중..." /> : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {previewSrc ? (
        <iframe
          title={title}
          src={previewSrc}
          className="h-[800px] w-full rounded-lg border"
        />
      ) : null}
    </div>
  );
};
